import {
  nonReactSetCurrentTime,
  useRealTimeStore,
} from '@hotjar/webapp/features/Playbacks/store/realTimeStore';
import { Store } from '../../../store/player/types';
import { showWorthWatchingTooltipIfNotSeen } from './showWorthWatchingTooltipIfNotSeen';
import { cancelNextRecordingCountdown } from './cancelNextRecordingCountdown';
import { setCurrentIframeContent } from './setCurrentIframeContent';
import { handleViewportResizeEvent } from './handleViewportResizeEvent';
import { onPlayTick } from './onPlayTick';

/**
 * Jump to a specific point in time for a recording.
 */
export const jumpTo = (store: Store, targetTime: number): void => {
  const {
    playTickInterval,
    playTimeoutId,
    playing,
    recording,
    setShowLoader,
    setDone,
    setShowCursor,
    setLastTickTime,
    setPageToStartProcess,
    setCurrentPageIndex,
    setPosition,
    setJumpToPause,
    setSkipNonMutationEvents,
  } = store.getState();

  if (!recording) return;

  setSkipNonMutationEvents(true);

  let time = targetTime;

  if (time < 0) {
    time = 0;
  }

  if (time > recording.duration) {
    time = recording.duration;
  }

  setLastTickTime(null); // We don't want to log interactions with timeline

  if (time < useRealTimeStore.getState().currentTime) {
    // It's a backward jump
    // we show the worthWatching tooltip
    showWorthWatchingTooltipIfNotSeen(store);
  }

  // Stop the next recording countdown if it's running.
  cancelNextRecordingCountdown(store);

  // We show the loading overlay until the page has loaded.
  setShowLoader(true);
  setDone(false);

  // Stop recording immediately.
  clearTimeout(playTimeoutId as number);

  // Find the index of the page that the targetTime belongs to
  const currentPageIndex = recording.page_visit_recordings.findIndex((pvr) => {
    return pvr.time_in_timeline <= time && time <= pvr.time_in_timeline + pvr.duration;
  });
  const currentStartPageContentId =
    recording.page_visit_recordings[currentPageIndex].start_page_content_identifier;

  // We go through all the pages to find the first that contains the same
  // page content identifier as the one the user moved to.
  let pageToStartProcess = currentPageIndex;
  for (let index = currentPageIndex; index >= 0; index--) {
    const pvr = recording.page_visit_recordings[index];
    if (pvr.start_page_content_identifier === currentStartPageContentId) {
      pageToStartProcess = index;
    }
  }

  setPageToStartProcess(pageToStartProcess);
  setCurrentPageIndex(currentPageIndex);

  // Load the correct page into the iframe.
  setCurrentIframeContent(store, pageToStartProcess, true);

  // Make sure that any change in viewport dimensions is caught (HJ-7316).
  handleViewportResizeEvent(store, {
    w: recording.page_visit_recordings[pageToStartProcess].window_width,
    h: recording.page_visit_recordings[pageToStartProcess].window_height,
  });

  if (!playing) {
    // Since we need to run onPlayTick once to reload all events,
    // set the currentTime to be 100ms before.
    nonReactSetCurrentTime(Math.max(0, time - playTickInterval));
    setJumpToPause(true);
  } else {
    nonReactSetCurrentTime(time);
  }

  setPosition(0, 0);
  setShowCursor(false);

  onPlayTick(store);
};
